import { useIonToast } from '@ionic/react';
import {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardService } from '../../../../service/ClipboardService';

export interface ClipboardProviderProps {
  clipboardService: ClipboardService;
  children: ReactElement;
}

export const ClipboardContext = createContext<ClipboardService | undefined>(
  undefined
);

export const ClipboardContextProvider = (
  props: ClipboardProviderProps
): ReactElement => {
  const { clipboardService, children } = props;
  const [presentToast] = useIonToast();

  const { i18n } = useTranslation();

  const copy = useCallback(
    (path: string, value: unknown): void => {
      clipboardService.copy(path, value);
      presentToast({
        message: i18n.t('common:copied_items'),
        color: 'success',
        duration: 1500,
      });
    },
    [clipboardService, presentToast, i18n]
  );

  const value = useMemo((): ClipboardService => {
    return {
      copy,
      paste: clipboardService.paste,
    };
  }, [clipboardService, copy]);

  return (
    <ClipboardContext.Provider value={value}>
      {children}
    </ClipboardContext.Provider>
  );
};

export const useClipboardContext = (): ClipboardService => {
  const context = useContext(ClipboardContext);
  return context as ClipboardService;
};
